import { useState, useCallback } from "react";
import Modal from "components/ui/modals";
import Select from "components/ui/Select";
import { useAllTemplates, useDeleteTemplate } from "http-hooks/ordtemplate/useOrdTemplate";
import { fetchTemplateDetails } from "services/ordtemplate";
import { TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

export default function LoadTemplateModal({ isOpen, onClose, vendorId, onLoadTemplate }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const { data: templates = [], isLoading: isLoadingTemplates } = useAllTemplates(vendorId);
  const { mutateAsync: deleteMutate, isPending: isDeleting } = useDeleteTemplate();

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  const handleLoad = useCallback(async () => {
    if (!selectedTemplateId) {
      toast.error("Please select a template");
      return;
    }

    setIsLoadingDetails(true);
    try {
      const details = await fetchTemplateDetails(selectedTemplateId);
      onLoadTemplate(details);
      toast.success("Template loaded successfully");
      onClose();
    } catch (error) {
      console.error("Failed to load template:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [selectedTemplateId, onLoadTemplate, onClose]);

  const handleDelete = useCallback(async () => {
    if (!selectedTemplateId) {
      toast.error("Please select a template to delete");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this template?")) return;

    try {
      await deleteMutate(selectedTemplateId);
      setSelectedTemplateId(null);
    } catch (error) {
      console.error("Failed to delete template:", error);
    }
  }, [selectedTemplateId, deleteMutate]);

  return (
    <Modal
      open={isOpen}
      setOpen={() => onClose()}
      maxWidth="md"
      header={
        <div>
          <h3 className="text-lg font-semibold leading-6 text-gray-900">Load Order Template</h3>
          <p className="mt-0.5 text-sm text-gray-500">Select an order template to populate the form.</p>
        </div>
      }
      footer={
        <div className="flex justify-between">
          <button
            type="button"
            onClick={handleDelete}
            disabled={!selectedTemplateId || isDeleting}
            className="inline-flex items-center gap-1 rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrashIcon className="h-4 w-4" />
            {isDeleting ? "Deleting..." : "Delete"}
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLoad}
              disabled={!selectedTemplateId || isLoadingDetails}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoadingDetails ? "Loading..." : "Load Template"}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Select
          label="Template"
          value={templateOptions.find((o) => o.value === selectedTemplateId) || null}
          onChange={(option) => setSelectedTemplateId(option?.value || null)}
          options={templateOptions}
          placeholder="Choose a template..."
          isLoading={isLoadingTemplates}
          isClearable
          isSearchable
        />

        {templates.length === 0 && !isLoadingTemplates && (
          <p className="text-sm text-gray-500 text-center py-4">
            No templates found{vendorId ? " for this vendor" : ""}.
          </p>
        )}
      </div>
    </Modal>
  );
}
